import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import type { Claim } from '../../types/researchDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { glassPanel, linkButton, secondaryButton } from '../../utils/demoUi';
import { claimSummary, outcomeLabels, passageFor, sourceById, statusLabels } from '../../utils/researchDemo';
import { ReviewForm } from './ReviewForm';
import { StatusPill } from './StatusPill';
export function EvidenceRecord({
  claim
}: {
  claim: Claim;
}) {
  const {
    state,
    dispatch,
    inspectSource
  } = useResearchDemo();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const source = sourceById(claim.sourceId);
  const passage = passageFor(claim.sourceId, claim.passageId);
  const canReview = state.ui.workspace === 'curation';
  useEffect(() => {
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [claim.id]);
  return <div data-id="element-2042" data-mp="10gdewqikeoh0">
      <button type="button" onClick={() => dispatch({
      type: 'openClaim',
      id: null
    })} className={secondaryButton} data-id="element-2043" data-mp="10g4y4njbmz0j">
        <ArrowLeft size={16} aria-hidden="true" data-id="element-2044" data-mp="vi3jcesxhluv" />
        Back
      </button>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2045" data-mp="19gtg1tbu5jip">
        <p className="font-mono text-sm font-semibold text-ink-muted" data-id="element-2046" data-mp="19ie6p7v14iba">
          Evidence record · {claim.id} · version {claim.version}
        </p>
        <StatusPill status={claim.status} data-id="element-2047" data-mp="1jeplc4i3dk8w" />
      </div>
      <h1 ref={headingRef} tabIndex={-1} className="mt-2 font-heading text-3xl font-bold leading-tight tracking-tight text-ink-dark outline-none" data-id="element-2048" data-mp="1jd67ekjwrlaa">
        {claimSummary(claim)}
      </h1>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-2049" data-mp="1t497bj13pu7z">
        <section aria-labelledby="interpretation-title" className={`${glassPanel} p-6`} data-id="element-2050" data-mp="1o70mtbshde3k">
          <h2 id="interpretation-title" className="text-sm font-semibold text-ink-muted" data-id="element-2051" data-mp="224z1kiaprcjl">
            Reviewer interpretation · current
          </h2>
          <p className="mt-2 text-lg leading-relaxed text-ink-dark" data-id="element-2052" data-mp="1x4kscwxdlwjz">{claim.interpretation}</p>
          <h2 className="mt-6 text-sm font-semibold text-ink-muted" data-id="element-2053" data-mp="ur4zw694ca9c">Scope and limitations</h2>
          <p className="mt-2 leading-relaxed text-ink-dark" data-id="element-2054" data-mp="zskkjx7dwer7">{claim.limitation}</p>
        </section>

        <section aria-labelledby="passage-title" className={`${glassPanel} p-6`} data-id="element-2055" data-mp="13smq4hg8psbr">
          <h2 id="passage-title" className="text-sm font-semibold text-ink-muted" data-id="element-2056" data-mp="18m7edhs177xr">
            Exact supporting passage · source text
          </h2>
          {passage && source ? <>
              <blockquote className="mt-3 border-l-4 border-navy/40 pl-4 font-serif text-[1.05rem] leading-relaxed text-ink-dark" data-id="element-2057" data-mp="1ih92caakm3yr">
                “{passage.text}”
              </blockquote>
              <p className="mt-3 text-sm text-ink-muted" data-id="element-2058" data-mp="1ri7b15vyezno">
                {source.id} · {passage.id} · {source.title} ({source.design})
              </p>
              <button type="button" onClick={() => inspectSource(source.id, passage.id)} className={`mt-4 ${linkButton}`} data-id="element-2059" data-mp="3tavcpasy9f1c">
                Open full source context
                <ArrowRight size={14} aria-hidden="true" data-id="element-2060" data-mp="3lsnk82o54x1x" />
              </button>
            </> : <p className="mt-2 text-sm text-rose-700" data-id="element-2061" data-mp="3gyjbbdvbeey7">The cited passage could not be found.</p>}
        </section>
      </div>

      {canReview && <div className="mt-8" data-id="element-2062" data-mp="380z6xw25o2jd">
          <ReviewForm claim={claim} data-id="element-2063" data-mp="37xuvcdmd24io" />
        </div>}

      <section aria-labelledby="versions-title" className="mt-10" data-id="element-2064" data-mp="8jrhk0rd3eep">
        <h2 id="versions-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2065" data-mp="3lpwjaadl50l">
          Previous versions
        </h2>
        {claim.previousVersions.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-2066" data-mp="3vrd1bt87qdh4">This is the first version of the record.</p> : <ol className="mt-3 space-y-3" data-id="element-2067" data-mp="2kadcx8ozkisd">
            {[...claim.previousVersions].reverse().map(version => <li key={version.version} className="rounded-xl border border-line bg-white/70 p-5" data-id="element-2068" data-mp="2faqy4utieuhg">
                <p className="text-xs font-semibold text-ink-muted" data-id="element-2069" data-mp="722bp7n9m2zj">
                  Version {version.version} · {statusLabels[version.status]} · {formatTimestamp(version.recordedAt)}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-dark" data-id="element-2070" data-mp="3u7tiq9300g8y">{version.interpretation}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-muted" data-id="element-2071" data-mp="22pq0q84r1yj">Limitation: {version.limitation}</p>
              </li>)}
          </ol>}
      </section>

      <section aria-labelledby="reviews-title" className="mt-10" data-id="element-2072" data-mp="urwlfgcafbfk">
        <h2 id="reviews-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2073" data-mp="uq2c6zadp7rb">
          Review history
        </h2>
        {claim.reviews.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-2074" data-mp="lq5hoh1b5eoc">
            {claim.status === 'approved' ? 'Sample record seeded as approved; no reviews recorded in this browser yet.' : 'No reviews recorded yet.'}
          </p> : <div className={`${glassPanel} mt-3 overflow-x-auto`} data-id="element-2075" data-mp="36fmjn25w1v5u">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-2076" data-mp="2xzdr771p6d8c">
              <caption className="sr-only" data-id="element-2077" data-mp="2tzmvqq1878xh">Reviews of {claim.id}</caption>
              <thead data-id="element-2078" data-mp="37yuw3m94kn39">
                <tr className="border-b border-line text-xs text-ink-muted" data-id="element-2079" data-mp="37zedn1uv59gr">
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2080" data-mp="32zs1kvqrtz9j">When</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2081" data-mp="32xp1fivwmpr3">Change</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2082" data-mp="2faqmipnfnpva">Outcome</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2083" data-mp="2ac5q71baxqc4">Rationale</th>
                </tr>
              </thead>
              <tbody data-id="element-2084" data-mp="2p1blie70qweq">
                {[...claim.reviews].reverse().map(review => <tr key={review.id} className="border-b border-line align-top last:border-b-0" data-id="element-2085" data-mp="2k7pildqnq6w4">
                    <td className="px-4 py-3 text-ink-muted whitespace-nowrap" data-id="element-2086" data-mp="1wi4ygrirp3l9">{formatTimestamp(review.at)}</td>
                    <td className="px-4 py-3 text-ink-dark" data-id="element-2087" data-mp="1rkcvn2gl3x2a">
                      v{review.priorVersion} ({statusLabels[review.priorStatus]}) → v{review.newVersion}
                      <span className="mt-1 block text-xs text-ink-muted" data-id="element-2088" data-mp="fjefynoltb0">Prior interpretation: {review.priorInterpretation}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-ink-dark whitespace-nowrap" data-id="element-2089" data-mp="98vckl0oyfre">{outcomeLabels[review.outcome]}</td>
                    <td className="px-4 py-3 leading-relaxed text-ink-dark" data-id="element-2090" data-mp="38orzcjts026j">{review.rationale}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </section>
    </div>;
}